import { css } from '@emotion/react';
import { FEE_ITEM_TYPE, formatCurrency, formatPoints } from 'shared';

import { SasIcon } from '../SasIcon';
import { theme } from '../theme';

import { Item } from './Item';
import { LegacyCartTotal } from './LegacyCartTotal';
import { PriceDetail } from './PriceDetail';
import { LegacyPriceItem } from './types';
import { createCartItem } from './utils';

const styles = {
  containerField: (isSelected: boolean, preventExpandedPaxContainer: boolean) => css`
    cursor: ${preventExpandedPaxContainer ? 'pointer' : 'cursor'};
    background: ${theme.colors.gray[0]};
    border-radius: ${isSelected ? `${theme.spacing.sm} ${theme.spacing.sm} 0 0` : theme.spacing.sm};
    margin-bottom: ${isSelected ? '0px' : theme.spacing.sm};
    padding: ${theme.spacing.md};
    width: 100%;
    display: flex;
    justify-content: space-between;
  `,

  expandedContainer: css`
    width: 100%;
    padding: 0 ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md};
    display: flex;
    flex-direction: column;
    background: ${theme.colors.gray[0]};
    margin-bottom: ${theme.spacing.sm};
    border-radius: 0 0 ${theme.borders.radius.md} ${theme.borders.radius.md};
  `,
  totalPriceContainer: css`
    margin-top: ${theme.spacing.sm};
    display: flex;
    justify-content: end;
  `,
  divider: css`
    margin-top: 4px;
    background-color: #80c5e5;
    width: 100%;
  `,
  noMargin: css`
    margin-bottom: 0;
  `,

  price: css`
    white-space: nowrap;
  `,
  taxes: css`
    font-size: ${theme.fontSize.sm};
    color: ${theme.colors.link};
    cursor: pointer;
  `,
  container: css`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${theme.spacing.sm};
  `,
  total: css`
    display: flex;
    justify-content: end;
  `,
  taxContainer: css`
    display: flex;
    justify-content: space-between;
  `,
};

type Props = {
  isSarRebooking?: boolean;
  pointOfSale: string;
  priceItem: LegacyPriceItem;
  selectedId: number | null;
  shouldDisplayPrice: boolean;
  handleExpandedPaxClick: (id: number) => void;
};
export function PriceField({
  priceItem,
  selectedId,
  pointOfSale,
  handleExpandedPaxClick,
  shouldDisplayPrice,
  isSarRebooking = false,
}: Props) {
  if (!priceItem) return undefined;

  const { expandable, id, title } = priceItem;

  const displayPrice = expandable && shouldDisplayPrice;

  const filteredPrices = priceItem.feeItems.filter(row => row.itemType !== FEE_ITEM_TYPE.Total);

  return (
    <>
      <div
        css={styles.containerField(selectedId === id, expandable)}
        onClick={() => displayPrice && handleExpandedPaxClick(id)}
      >
        <b css={styles.noMargin}>{title}</b>
        {displayPrice && <SasIcon name="in--arrow-down" color="var(--color-gray-5)" />}
      </div>
      {selectedId === id && (
        <div css={styles.expandedContainer}>
          {filteredPrices.map((row, index) => {
            const { label, amount, details, priceType, discountedPoints } = row;

            const priceListItem = createCartItem(row);
            const hasDiscount = discountedPoints ? discountedPoints < amount : false;

            return (
              <div key={`${label}-${index}`}>
                {details !== undefined && priceListItem !== undefined ? (
                  <div css={styles.taxContainer}>
                    {<PriceDetail priceListItem={priceListItem} pointOfSale={pointOfSale} />}
                    <span>{formatCurrency(amount, pointOfSale)}</span>
                  </div>
                ) : (
                  <Item
                    discountedPoints={hasDiscount ? formatPoints(discountedPoints ?? 0, pointOfSale) : undefined}
                    label={label}
                    price={
                      priceType === 'points' ? formatPoints(amount, pointOfSale) : formatCurrency(amount, pointOfSale)
                    }
                  />
                )}
              </div>
            );
          })}
          <hr css={styles.divider} />
          <div css={styles.total}>
            <LegacyCartTotal
              pos={pointOfSale}
              feeItems={priceItem.feeItems}
              align="right"
              small
              isSarRebooking={isSarRebooking}
            />
          </div>
        </div>
      )}
    </>
  );
}
